const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export function galleryReleaseVelocity(samples, releasedAt) {
  const last = samples[samples.length - 1];
  if (!last || releasedAt - last.time > 90) return 0;
  const first = samples.find(sample => last.time - sample.time <= 90) || last;
  const elapsed = last.time - first.time;
  if (elapsed < 8) return 0;
  return clamp((last.position - first.position) / elapsed * 1000, -2600, 2600);
}

export function projectGalleryOffset(position, velocity) {
  return position + clamp(velocity * .24, -560, 560);
}

export function enableMouseDragRail(rail, { momentum = false } = {}) {
  if (!rail) return { move: () => {}, destroy: () => {} };

  let gesture = null;
  let motion = null;
  let drift = null;
  let hoverDirection = 0;
  let savedSnap = '';
  let savedBehavior = '';
  let suppressClick = false;
  let clickTimer = null;

  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const maxScroll = () => Math.max(0, rail.scrollWidth - rail.clientWidth);
  const snapPoints = () => Array.from(rail.children)
    .filter(item => item.matches('img, video'))
    .map(item => clamp(item.offsetLeft - (rail.clientWidth - item.offsetWidth) / 2, 0, maxScroll()));
  const nearestIndex = (points, offset) => points.reduce((closest, point, index) =>
    Math.abs(point - offset) < Math.abs(points[closest] - offset) ? index : closest, 0);

  const beginControlledScroll = () => {
    savedSnap = rail.style.scrollSnapType;
    savedBehavior = rail.style.scrollBehavior;
    rail.style.scrollSnapType = 'none';
    rail.style.scrollBehavior = 'auto';
    rail.classList.add('is-gallery-motion');
  };

  const endControlledScroll = (settle = true) => {
    if (savedSnap) rail.style.scrollSnapType = savedSnap;
    else rail.style.removeProperty('scroll-snap-type');
    if (savedBehavior) rail.style.scrollBehavior = savedBehavior;
    else rail.style.removeProperty('scroll-behavior');
    rail.classList.remove('is-gallery-motion', 'is-mouse-dragging');
    if (settle) rail.dispatchEvent(new Event('home-gallery-motion-end'));
  };

  const stopMotion = (settle = false) => {
    if (!motion) return { velocity: 0, target: rail.scrollLeft };
    cancelAnimationFrame(motion.frame);
    const previous = motion;
    motion = null;
    endControlledScroll(settle);
    return previous;
  };

  const stopDrift = () => {
    if (!drift) return;
    cancelAnimationFrame(drift.frame);
    drift = null;
    endControlledScroll(false);
  };

  const startDrift = () => {
    if (!hoverDirection || motion || gesture || drift || reducedMotion()) return;
    beginControlledScroll();
    const state = { frame: null, lastTime: 0 };
    drift = state;
    const step = (time) => {
      if (drift !== state || !hoverDirection) return;
      const elapsed = state.lastTime ? clamp((time - state.lastTime) / 1000, 0, .032) : 0;
      state.lastTime = time;
      rail.scrollLeft += hoverDirection * 78 * elapsed;
      rail.dispatchEvent(new Event('home-gallery-motion-wrap'));
      state.frame = requestAnimationFrame(step);
    };
    state.frame = requestAnimationFrame(step);
  };

  const springTo = (target, velocity = 0) => {
    stopMotion();
    beginControlledScroll();
    target = clamp(target, 0, maxScroll());
    if (reducedMotion()) {
      rail.scrollLeft = target;
      endControlledScroll();
      return;
    }
    const state = { target, velocity, position: rail.scrollLeft, lastTime: 0, frame: null };
    motion = state;
    const step = (time) => {
      if (motion !== state) return;
      const elapsed = state.lastTime ? clamp((time - state.lastTime) / 1000, 0, .032) : 1 / 60;
      state.lastTime = time;
      const acceleration = 165 * (state.target - state.position) - 23 * state.velocity;
      state.velocity += acceleration * elapsed;
      state.position = clamp(state.position + state.velocity * elapsed, 0, maxScroll());
      rail.scrollLeft = state.position;
      if (Math.abs(state.target - state.position) < .55 && Math.abs(state.velocity) < 9) {
        rail.scrollLeft = state.target;
        motion = null;
        endControlledScroll();
        startDrift();
      } else {
        state.frame = requestAnimationFrame(step);
      }
    };
    state.frame = requestAnimationFrame(step);
  };

  const move = (direction) => {
    if (direction !== -1 && direction !== 1) return;
    const points = snapPoints();
    if (points.length < 2) return;
    stopDrift();
    rail.dispatchEvent(new Event('home-gallery-control'));
    const anchor = motion?.target ?? rail.scrollLeft;
    const projected = anchor + direction * rail.clientWidth * .88;
    let index = nearestIndex(points, projected);
    if (Math.abs(points[index] - anchor) < rail.clientWidth * .55) {
      index = clamp(index + direction, 0, points.length - 1);
    }
    const previousVelocity = motion?.velocity || 0;
    const velocity = direction * Math.max(2100, previousVelocity * direction);
    springTo(points[index], velocity);
  };

  const hover = (direction) => {
    if (direction !== -1 && direction !== 1) return;
    if (hoverDirection === direction) return;
    stopDrift();
    hoverDirection = direction;
    rail.dispatchEvent(new Event('home-gallery-control'));
    startDrift();
  };

  const stopHover = () => {
    const direction = hoverDirection;
    hoverDirection = 0;
    if (!drift) return;
    stopDrift();
    const points = snapPoints();
    if (points.length) springTo(points[nearestIndex(points, rail.scrollLeft)], direction * 78);
  };

  const onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) {
      hoverDirection = 0;
      stopDrift();
      stopMotion();
      return;
    }
    const wasControlled = Boolean(drift || motion);
    hoverDirection = 0;
    stopDrift();
    stopMotion();
    if (wasControlled) beginControlledScroll();
    gesture = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      scrollLeft: rail.scrollLeft,
      controlled: wasControlled,
      dragging: false,
      samples: [],
    };
  };

  const onPointerMove = (event) => {
    if (!gesture || event.pointerId !== gesture.id) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (!gesture.dragging) {
      if (Math.abs(dx) < 6 || Math.abs(dx) <= Math.abs(dy)) return;
      gesture.dragging = true;
      if (!gesture.controlled) beginControlledScroll();
      rail.classList.add('is-mouse-dragging');
      rail.setPointerCapture(event.pointerId);
    }
    rail.scrollLeft = gesture.scrollLeft - dx;
    const time = performance.now();
    gesture.samples.push({ position: rail.scrollLeft, time });
    gesture.samples = gesture.samples.filter(sample => time - sample.time <= 100);
    event.preventDefault();
  };

  const endGesture = (event) => {
    if (!gesture || event.pointerId !== gesture.id) return;
    const ended = gesture;
    gesture = null;
    if (!ended.dragging) {
      if (ended.controlled) endControlledScroll();
      return;
    }
    if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
    rail.classList.remove('is-mouse-dragging');
    suppressClick = true;
    window.clearTimeout(clickTimer);
    clickTimer = window.setTimeout(() => { suppressClick = false; }, 80);
    if (!momentum) {
      endControlledScroll();
      return;
    }
    const points = snapPoints();
    if (!points.length) {
      endControlledScroll();
      return;
    }
    const velocity = event.type === 'pointercancel' || reducedMotion()
      ? 0 : galleryReleaseVelocity(ended.samples, performance.now());
    const projected = projectGalleryOffset(rail.scrollLeft, velocity);
    const target = points[nearestIndex(points, projected)];
    endControlledScroll(false);
    springTo(target, velocity);
  };

  const onClick = (event) => {
    if (!suppressClick) return;
    suppressClick = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  const onNativeScrollInput = () => {
    hoverDirection = 0;
    stopDrift();
    stopMotion();
  };

  const destroy = () => {
    hoverDirection = 0;
    stopDrift();
    stopMotion();
    if (gesture?.dragging) endControlledScroll(false);
    gesture = null;
    window.clearTimeout(clickTimer);
    rail.removeEventListener('pointerdown', onPointerDown);
    rail.removeEventListener('pointermove', onPointerMove);
    rail.removeEventListener('pointercancel', endGesture);
    rail.removeEventListener('click', onClick, true);
    rail.removeEventListener('wheel', onNativeScrollInput);
    rail.removeEventListener('touchstart', onNativeScrollInput);
    window.removeEventListener('pointerup', endGesture);
    window.removeEventListener('blur', onNativeScrollInput);
  };

  rail.addEventListener('pointerdown', onPointerDown);
  rail.addEventListener('pointermove', onPointerMove);
  rail.addEventListener('pointercancel', endGesture);
  rail.addEventListener('click', onClick, true);
  rail.addEventListener('wheel', onNativeScrollInput, { passive: true });
  rail.addEventListener('touchstart', onNativeScrollInput, { passive: true });
  window.addEventListener('pointerup', endGesture);
  window.addEventListener('blur', onNativeScrollInput);

  return { move, hover, stopHover, destroy };
}
